import { buildPackIaReport, type PackIaData } from "../api/client";
import { buildObjectiveReportMetrics, fallbackObjectiveSummary, generateObjectiveSummary } from "../api/summary";
import { renderPackIaHtml } from "../html/template";
import type { LogEvent, SectionProgress } from "./types";

export interface PipelineOptions {
  rememberme: string;
  centerId: string;
  centerName?: string;
  year: number;
  month: number;
  signal?: AbortSignal;
  onLog: (event: LogEvent) => void;
  onProgress: (progress: SectionProgress[]) => void;
}

export interface PipelineResult {
  data: PackIaData;
  html: string;
  htmlBlob: Blob;
  filenameBase: string;
}

function now(): string {
  return new Date().toLocaleTimeString("es-ES", { hour12: false });
}

async function loadBrandLogo(signal?: AbortSignal): Promise<string | undefined> {
  try {
    const response = await fetch("/logo_pack_ia.svg", { signal });
    if (!response.ok) return undefined;
    const bytes = new Uint8Array(await response.arrayBuffer());
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return `data:image/svg+xml;base64,${btoa(binary)}`;
  } catch {
    return undefined;
  }
}

function describeErr(error: unknown): string {
  const err = error as any;
  if (err?.response?.status) return `HTTP ${err.response.status}${err.response?.data?.error ? ` — ${err.response.data.error}` : ""}`;
  return err?.message || String(error);
}

export async function runPipeline(options: PipelineOptions): Promise<PipelineResult> {
  const progress: SectionProgress[] = [
    { name: "fetch", title: "Recogida de datos", state: "active" },
    { name: "summary", title: "Resumen objetivo con IA", state: "pending" },
    { name: "html", title: "Generación HTML", state: "pending" },
  ];
  const log = (level: LogEvent["level"], message: string) => options.onLog({ level, ts: now(), message });
  options.onProgress([...progress]);

  let data: PackIaData;
  try {
    data = await buildPackIaReport({
      rememberme: options.rememberme,
      centerId: options.centerId,
      centerName: options.centerName,
      year: options.year,
      month: options.month,
      signal: options.signal,
      onLog: (message, level) => log(level || "info", message),
    });

    progress[0].state = "done";
    progress[1].state = "active";
    options.onProgress([...progress]);
  } catch (error) {
    const message = describeErr(error);
    progress[0].state = "error";
    progress[0].error = message;
    options.onProgress([...progress]);
    log("err", `Recogida falló: ${message}`);
    throw error;
  }

  try {
    log("call", "POST /api/summary · generando resumen objetivo con IA");
    data.summary = await generateObjectiveSummary(data, options.signal);
    log("ok", "Resumen objetivo generado con IA");
    progress[1].state = "done";
  } catch (error) {
    // The report remains usable if the optional generation service is unavailable.
    data.summary = fallbackObjectiveSummary(buildObjectiveReportMetrics(data));
    progress[1].state = "error";
    progress[1].error = describeErr(error);
    log("err", `Resumen IA no disponible; se usa un resumen automático: ${describeErr(error)}`);
  }
  progress[2].state = "active";
  options.onProgress([...progress]);

  try {
    log("step", "Generando HTML listo para imprimir como PDF");
    const html = renderPackIaHtml(data, { brandLogoUri: await loadBrandLogo(options.signal) });
    const htmlBlob = new Blob([html], { type: "text/html;charset=utf-8" });
    progress[2].state = "done";
    options.onProgress([...progress]);
    log("ok", `HTML ${(htmlBlob.size / 1024).toFixed(1)} KB generado`);
    const safeCenter = data.center.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "centro";
    return { data, html, htmlBlob, filenameBase: `packia-${safeCenter}-${data.year}-${String(data.month).padStart(2, "0")}` };
  } catch (error) {
    const message = describeErr(error);
    progress[2].state = "error";
    progress[2].error = message;
    options.onProgress([...progress]);
    log("err", `Generación HTML falló: ${message}`);
    throw error;
  }
}
